/**
 * LiveStatus Component
 *
 * Displays real-time status indicators in the TopBar:
 * - Online device/modem count
 * - Running execution count
 */

import React from 'react';
import { Loader2 } from 'lucide-react';
import { useLiveStatus } from '@/hooks/useLiveStatus';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';

export function LiveStatus() {
  const {
    onlineDevices,
    totalDevices,
    onlineModems,
    totalModems,
    runningExecutions,
    pendingExecutions,
    devicesLoading,
    devicesError,
  } = useLiveStatus();

  // Show skeleton while loading
  if (devicesLoading) {
    return (
      <div className="hidden lg:flex items-center gap-4 px-3 py-2 bg-background rounded-lg border border-border">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    );
  }

  // Show error state
  if (devicesError) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="hidden lg:flex items-center gap-2 px-3 py-2 bg-background rounded-lg border border-border">
              <div className="w-2 h-2 bg-accent-red rounded-full" />
              <span className="text-sm text-muted-foreground">Offline</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Failed to fetch device status</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const totalRunning = runningExecutions + pendingExecutions;

  return (
    <TooltipProvider>
      <div className="hidden lg:flex items-center gap-4 px-3 py-2 bg-background rounded-lg border border-border dark:border-border-subtle">
        {/* Online Devices/Modems */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2 cursor-default">
              <div className="w-2 h-2 bg-status-online rounded-full animate-glow-pulse" />
              <span className="text-sm text-muted-foreground">
                {onlineDevices} online
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-sm">
              <p className="font-medium">Device Status</p>
              <p className="text-muted-foreground">
                {onlineDevices} of {totalDevices} locations online
              </p>
              <p className="text-muted-foreground">
                {onlineModems} of {totalModems} modems online
              </p>
            </div>
          </TooltipContent>
        </Tooltip>

        <div className="w-px h-4 bg-border" />

        {/* Running Executions */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2 cursor-default">
              {totalRunning > 0 ? (
                <div className="w-2 h-2 bg-accent-blue rounded-full animate-glow-pulse" />
              ) : (
                <div className="w-2 h-2 bg-muted-foreground/30 rounded-full" />
              )}
              <span className="text-sm text-muted-foreground">
                {totalRunning} running
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-sm">
              <p className="font-medium">Execution Status</p>
              {runningExecutions > 0 && (
                <p className="text-muted-foreground">
                  {runningExecutions} executing
                </p>
              )}
              {pendingExecutions > 0 && (
                <p className="text-muted-foreground">
                  {pendingExecutions} pending
                </p>
              )}
              {totalRunning === 0 && (
                <p className="text-muted-foreground">No active executions</p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
